import { API_BASE_URL } from '../config';

class StaffService {
    constructor() {
        const baseUrl = (API_BASE_URL || '').replace(/\/+$/, '');
        this.baseUrl = baseUrl;
        
        // As rotas mostradas no urls.py:
        // path('fin/taxas/', TaxaFinCorridaViewSet.as_view({'get': 'list', 'post': 'create'}))
        // path('fin/taxas/<int:pk>/', TaxaFinCorridaViewSet.as_view({'get': 'retrieve', 'put': 'update', 'patch': 'partial_update', 'delete': 'destroy'}))
        // path('fin/taxas/corrida/<int:id_corrida>/', RetrieveTaxasPorCorridaView.as_view())
        this.endpoints = {
            usuarios: `${baseUrl}/core/user_credentials/`,
            usuarioDetail: (pk) => `${baseUrl}/core/user_credentials/${pk}/`,
            eventos: `${baseUrl}/core/registro_corrida/retrieve_eventos`,
            financeiroGeral: `${baseUrl}/core/fin/`,
            infoAdicional: `${baseUrl}/core/info_adicional_user/`,
            infoAdicionalDetail: (pk) => `${baseUrl}/core/info_adicional_user/${pk}/`,
            infoAdicionalRetrieve: (usuarioId) => `${baseUrl}/core/info_adicional_usuario/retrieve/${usuarioId}/`,
            userUpdate: (pk) => `${baseUrl}/core/user/update/${pk}/`,
            
            // Taxas (tenta com /core/fin/taxas/ e aceita fallback para /fin/taxas/)
            taxas: `${baseUrl}/core/fin/taxas/`,
            taxaDetail: (pk) => `${baseUrl}/core/fin/taxas/${pk}/`,
            taxaPorCorrida: (idCorrida) => `${baseUrl}/core/fin/taxas/corrida/${idCorrida}/`,

            // Disparo geral de e-mails para todos os usuários
            disparoGeral: `${baseUrl}/core/staff/disparo_email/`,
            disparoGeralAlt: `${baseUrl}/core/user_credentials/disparo_geral/`,

            // Reset de senha
            passwordResetRequest: `${baseUrl}/core/auth/password-reset/request/`,
            resetSenhaEmail: `${baseUrl}/core/reset-senha_email/`,
        };
    }

    getAuthHeaders() {
        const token = sessionStorage.getItem('brv-user-token');
        return {
            'Content-Type': 'application/json; charset=UTF-8',
            ...(token ? { 'Authorization': `Token ${token}` } : {})
        };
    }

    // Helper para tentar a URL principal com /core/ e fallback sem /core/ caso retorne 404
    async fetchWithFallback(url, options = {}) {
        const response = await fetch(url, {
            ...options,
            headers: {
                ...this.getAuthHeaders(),
                ...(options.headers || {})
            }
        });

        // Se der 404 e tiver /core/fin/, tenta sem /core/
        if (response.status === 404 && url.includes('/core/fin/')) {
            const alternativeUrl = url.replace('/core/fin/', '/fin/');
            try {
                const altResponse = await fetch(alternativeUrl, {
                    ...options,
                    headers: {
                        ...this.getAuthHeaders(),
                        ...(options.headers || {})
                    }
                });
                return altResponse;
            } catch {
                return response;
            }
        }

        return response;
    }

    async getUsuarios() {
        try {
            const res = await this.fetchWithFallback(this.endpoints.usuarios, { method: 'GET' });
            if (!res.ok && res.status === 404) {
                return this.fetchWithFallback(this.endpoints.usuarios.replace(/\/$/, ''), { method: 'GET' });
            }
            return res;
        } catch (error) {
            console.error('Erro ao buscar usuários:', error);
            throw error;
        }
    }

    /**
     * Busca dados detalhados de um usuário pelo ID (GET /core/user_credentials/<pk>/)
     */
    async getUsuarioById(id) {
        const url = this.endpoints.usuarioDetail(id);
        return this.fetchWithFallback(url, { method: 'GET' });
    }

    /**
     * Recupera as informações adicionais do usuário pelo ID do usuário
     * GET /core/info_adicional_usuario/retrieve/<int:usuario_id>/
     */
    async getInfoAdicionalUsuario(usuarioId) {
        const url = this.endpoints.infoAdicionalRetrieve(usuarioId);
        return this.fetchWithFallback(url, { method: 'GET' });
    }

    /**
     * Busca dados completos do usuário incluindo CPF e telefone (GET /core/user_data/<id>/)
     */
    async getUserData(id) {
        const url = `${this.baseUrl}/core/user_data/${id}/`;
        return this.fetchWithFallback(url, { method: 'GET' });
    }

    /**
     * Utiliza o endpoint /core/user/update/<int:pk>/ exclusivamente para atualizar
     * as informações adicionais do usuário (onde <int:pk> é o id do usuário).
     */
    async atualizarInfoAdicional(userIdOrData, maybeData) {
        let userId;
        let data;

        if (typeof userIdOrData === 'object' && !maybeData) {
            data = userIdOrData;
            userId = data.id_usuario || data.id || data.pk;
        } else {
            userId = userIdOrData;
            data = maybeData;
        }

        const body = typeof data === 'string' ? data : JSON.stringify(data);
        const url = `${this.baseUrl}/core/user/update/${userId}/`;

        let response = await this.fetchWithFallback(url, {
            method: 'PATCH',
            body: body
        });

        if (response.status === 405) {
            response = await this.fetchWithFallback(url, {
                method: 'PUT',
                body: body
            });
        }

        if (response.status === 405) {
            response = await this.fetchWithFallback(url, {
                method: 'POST',
                body: body
            });
        }

        return response;
    }

    /**
     * Atualiza dados de um usuário pelo ID (PATCH com fallback para PUT)
     */
    async atualizarUsuario(id, data) {
        const body = typeof data === 'string' ? data : JSON.stringify(data);
        const url = this.endpoints.usuarioDetail(id);

        let response = await this.fetchWithFallback(url, {
            method: 'PATCH',
            body: body
        });

        if (response.status === 405) {
            response = await this.fetchWithFallback(url, {
                method: 'PUT',
                body: body
            });
        }

        return response;
    }

    /**
     * Deleta a conta de um usuário pelo ID (DELETE)
     */
    async deletarUsuario(id) {
        const url = this.endpoints.usuarioDetail(id);
        return this.fetchWithFallback(url, {
            method: 'DELETE'
        });
    }

    /**
     * GET fin/taxas/ (list)
     * Retorna a listagem de todas as taxas cadastradas
     */
    async getTaxasGerais() {
        return this.fetchWithFallback(this.endpoints.taxas, {
            method: 'GET'
        });
    }

    /**
     * GET fin/taxas/corrida/<int:id_corrida>/ (RetrieveTaxasPorCorridaView)
     * Método habilitado: GET apenas
     */
    async getTaxaCorrida(idCorrida) {
        return this.fetchWithFallback(this.endpoints.taxaPorCorrida(idCorrida), {
            method: 'GET'
        });
    }

    /**
     * GET fin/taxas/<int:pk>/ (retrieve)
     */
    async getTaxaPorId(pk) {
        return this.fetchWithFallback(this.endpoints.taxaDetail(pk), {
            method: 'GET'
        });
    }

    /**
     * POST fin/taxas/ (create)
     * Cria um novo registro de taxa
     */
    async criarTaxa(data) {
        return this.fetchWithFallback(this.endpoints.taxas, {
            method: 'POST',
            body: typeof data === 'string' ? data : JSON.stringify(data)
        });
    }

    /**
     * PATCH fin/taxas/<int:pk>/ (partial_update) ou PUT (update)
     * Atualiza uma taxa existente pelo seu PK/ID
     */
    async atualizarTaxa(pk, data) {
        const body = typeof data === 'string' ? data : JSON.stringify(data);
        const patchRes = await this.fetchWithFallback(this.endpoints.taxaDetail(pk), {
            method: 'PATCH',
            body
        });

        if (patchRes.ok || patchRes.status !== 405) {
            return patchRes;
        }

        // Se PATCH não for permitido pelo backend, tenta PUT
        return this.fetchWithFallback(this.endpoints.taxaDetail(pk), {
            method: 'PUT',
            body
        });
    }

    /**
     * Orquestrador inteligente para salvar taxa de uma corrida considerando
     * que RetrieveTaxasPorCorridaView aceita apenas GET,
     * enquanto create é POST em fin/taxas/ e update é PATCH/PUT em fin/taxas/<pk>
     */
    async salvarTaxaCorrida(idCorrida, data, taxaPk = null) {
        let pk = taxaPk;

        // Se o PK da taxa não foi fornecido diretamente, tenta obter via GET fin/taxas/corrida/<id_corrida>/
        if (!pk) {
            try {
                const resConsulta = await this.getTaxaCorrida(idCorrida);
                if (resConsulta.ok) {
                    const taxaData = await resConsulta.json();
                    pk = taxaData?.id || taxaData?.pk;
                }
            } catch (err) {
                console.warn(`Não foi possível consultar taxa da corrida ${idCorrida} antes de salvar:`, err);
            }
        }

        // Se temos o PK da taxa existente, fazemos PATCH fin/taxas/<pk>/
        if (pk) {
            return this.atualizarTaxa(pk, data);
        }

        // Se não existe taxa para essa corrida, criamos via POST fin/taxas/
        return this.criarTaxa({
            ...data,
            corrida: idCorrida,
            id_corrida: idCorrida
        });
    }

    async getTodasCorridas() {
        return this.fetchWithFallback(this.endpoints.eventos, {
            method: 'GET'
        });
    }

    async getFinanceiroGeral(corridaId = '') {
        const url = corridaId ? `${this.endpoints.financeiroGeral}${corridaId}` : this.endpoints.financeiroGeral;
        return this.fetchWithFallback(url, {
            method: 'GET'
        });
    }

    /**
     * Dispara e-mail em massa para todos os usuários cadastrados na plataforma ou grupo filtrado
     * @param {Object|string} data - { subject, message_html, message_text, tipo_usuario, user_ids }
     */
    async dispararEmailGeral(data) {
        const body = typeof data === 'string' ? data : JSON.stringify(data);

        // Tenta endpoint principal /core/staff/disparo_email/
        let response = await this.fetchWithFallback(this.endpoints.disparoGeral, {
            method: 'POST',
            body: body
        });

        // Se retornar 404, tenta endpoint alternativo /core/user_credentials/disparo_geral/
        if (response.status === 404) {
            response = await this.fetchWithFallback(this.endpoints.disparoGeralAlt, {
                method: 'POST',
                body: body
            });
        }

        return response;
    }

    /**
     * Solicita o envio do e-mail de recuperação/reset de senha para um usuário
     * @param {string} email
     * @param {string} tipoUsuario
     */
    async solicitarResetSenha(email, tipoUsuario = "corredor") {
        const body = JSON.stringify({
            email: String(email || "").trim(),
            tipo_usuario: tipoUsuario
        });

        // 1. Tenta endpoint padrão de solicitação de reset (/core/auth/password-reset/request/)
        let response = await this.fetchWithFallback(this.endpoints.passwordResetRequest, {
            method: 'POST',
            body
        });

        // 2. Se não existir (404), tenta endpoint alternativo de reset por email (/core/reset-senha_email/)
        if (response.status === 404) {
            response = await this.fetchWithFallback(this.endpoints.resetSenhaEmail, {
                method: 'PATCH',
                body
            });
        }

        return response;
    }
}

export default StaffService;
